import { FC } from 'react'
import { HrSupportFeedbackTable } from './HrSupportFeedbackTable'
import { useAppraisalsWithFeedbackByCountryAndFilters } from '../../../../graphql/meeting/hooks/useAppraisalsWithFeedback'
import { Country } from '../../../../types'
import { useHrSupportStore } from '../HrSupportStore'
import shallow from 'zustand/shallow'
import { useAppraisalsWithFeedbackCounter } from '../../../../graphql/meeting/hooks/useAppraisalsWithFeedbackCounter'
import { HrSupportFeedbackInfoBox } from './HrSupportFeedbackInfoBox'
import { useDateTime } from '../../../../shared/useDateTime'
import { useAuth } from '../../../../context/auth-context'
import { ShowMoreButton } from '../../../reports/table/ShowMoreButton'
import { NetworkStatus } from '@apollo/client'

export const HrSupportFeedbackTab: FC = () => {
  const { user } = useAuth()
  const [selected, filters] = useHrSupportStore((state) => [state.selected, state.feedbackTabFilters], shallow)
  const { formatDateToIsoString } = useDateTime(user)

  const { data, error, loading, fetchMore, networkStatus } = useAppraisalsWithFeedbackByCountryAndFilters(
    selected.value as Country,
    {
      ...filters,
      companyFromDate: filters.companyFromDate && formatDateToIsoString(filters.companyFromDate),
      companyToDate: filters.companyToDate && formatDateToIsoString(filters.companyToDate, undefined, 1),
      employeeFromDate: filters.employeeFromDate && formatDateToIsoString(filters.employeeFromDate),
      employeeToDate: filters.employeeToDate && formatDateToIsoString(filters.employeeToDate, undefined, 1),
    },
    {},
  )
  const { data: counterData, error: counterError, loading: counterLoading } = useAppraisalsWithFeedbackCounter(selected.value as Country)

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore
  const showMoreVisible =
    !!data?.getAppraisalsWithFeedbackByCountryAndFilters.hasNextPage && ((!loading && !counterLoading) || isLoadingMore)

  const loadMore = () => {
    if (data && data.getAppraisalsWithFeedbackByCountryAndFilters.hasNextPage) {
      fetchMore({
        variables: {
          pagination: {
            cursorMeetingId: data.getAppraisalsWithFeedbackByCountryAndFilters.endCursorMeetingId,
            cursorConfirmationDate: data.getAppraisalsWithFeedbackByCountryAndFilters.endCursorConfirmationDate,
          },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult
          }

          const previousEdges = previousResult.getAppraisalsWithFeedbackByCountryAndFilters.edges
          const fetchMoreEdges = fetchMoreResult.getAppraisalsWithFeedbackByCountryAndFilters.edges

          fetchMoreResult.getAppraisalsWithFeedbackByCountryAndFilters.edges = [...previousEdges, ...fetchMoreEdges]

          return { ...fetchMoreResult }
        },
      })
    }
  }

  return (
    <>
      <HrSupportFeedbackInfoBox
        counterLoading={counterLoading}
        counter={!!counterData ? counterData.getAppraisalsWithFeedbackToResolveCounterByCountry : undefined}
      />

      <HrSupportFeedbackTable
        filteredData={data ? data.getAppraisalsWithFeedbackByCountryAndFilters.edges : undefined}
        feedbackToResolveCounter={!!counterData ? counterData.getAppraisalsWithFeedbackToResolveCounterByCountry : undefined}
        isLoading={loading || counterLoading}
        isLoadingMore={isLoadingMore}
        error={error || counterError}
        showMoreButtonVisible={showMoreVisible}
      />

      <ShowMoreButton visible={showMoreVisible} loadingMore={isLoadingMore} onLoadMore={loadMore} />
    </>
  )
}
